import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowRight, Calendar, Check, Clock, Globe, Inbox, Loader2, Lock, Mail, MessageSquare, RefreshCw, Send, Sparkles, Target, TrendingDown, Wand2, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { GoogleSignInButton } from "@/components/landing/GoogleSignInButton";
import { MicrosoftSignInButton } from "@/components/landing/MicrosoftSignInButton";
import { lovable } from "@/integrations/lovable/index";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/motion/Reveal";
import { MagneticButton } from "@/components/motion/MagneticButton";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/hooks/useAnalyticsTracking";
import { TrainingPlayground } from "@/components/landing/TrainingPlayground";

import { BookingPagePreviewBody, BookingPagePreviewLightbox } from "@/components/landing/BookingPagePreviewLightbox";

export type TrainingTrialHandle = {
  focusInput: (source?: "hero" | "demo" | "scroll") => void;
};

type TrackFn = (name: string, props?: Record<string, unknown>) => void;

function deriveHostname(value: string): string | null {
  const t = value.trim();
  if (!t) return null;
  try {
    const withProto = /^https?:\/\//i.test(t) ? t : `https://${t}`;
    return new URL(withProto).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

type EmailObject = {
  subject: string;
  greeting: string;
  hook: string;
  value: string;
  cta: string;
  signoff: string;
  ps?: string;
  learnMore?: { label: string; url: string };
  personaName?: string;
  personaRole?: string;
  personaCompany?: string;
  whyNow?: string;
};

type SenderIdentity = {
  company: string;
  hostname: string;
  address: string;
  url: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  colorScheme?: "light" | "dark";
  fontFamily?: string;
  colorSource?: "scraped" | "derived";
  logoSource?: "scraped" | "favicon";
};

type RevenueGap = {
  accountsMissed: number;
  competitorTargeting: string[];
  pipelineLowUsd: number;
  pipelineHighUsd: number;
  // Derivation inputs — server computes pipeline from these, UI shows the math.
  icpUniverseEstimate?: number;
  assumedAcvUsdLow?: number;
  assumedAcvUsdHigh?: number;
  conversionRateLow?: number;
  conversionRateHigh?: number;
};

type LiveTarget = {
  company: string;
  signal: string;
  matchPct: number;
};

type SequenceStep = {
  day: number;     // 3 | 6 | 10 | 15
  purpose: string; // "Reframe" | "Proof" | "Single question" | "Soft close"
  subject: string;
  teaser: string;
};

type TrialResult = {
  icp: { title: string; summary: string };
  pains: string[];
  angle: string;
  opener: string[];
  email?: EmailObject;
  sender?: SenderIdentity;
  revenueGap?: RevenueGap;
  liveTargets?: LiveTarget[];
  sequence?: SequenceStep[];
  
};

const TRIAL_PREVIEW_STORAGE_KEY = "autopitch.trial_preview_v1";

type Stage = "scrape" | "analyze";
type StageStatus = "pending" | "active" | "done";
type StageState = Record<Stage, StageStatus>;

type State =
  | { kind: "idle" }
  | { kind: "loading"; stages: StageState; startedAt: number }
  | {
      kind: "result";
      data: TrialResult;
      hostname: string | null;
      // True until the heavy `training-trial-preview` payload lands.
      // While true, sections that depend on email/sequence/onePager show
      // skeletons instead of being missing.
      pending?: boolean;
    }
  | { kind: "error"; message: string };

const INITIAL_STAGES: StageState = {
  scrape: "active",
  analyze: "pending",
};

// Client-side fallback that mirrors the server logic — only used when the
// server omits sender for any reason. The server is the source of truth.
function clientDeriveSender(hostname: string | null): SenderIdentity {
  if (!hostname) {
    return { company: "your team", hostname: "", address: "you@yourcompany.com", url: "" };
  }
  const host = hostname.replace(/^www\./i, "");
  const root = host.split(".")[0] || "";
  const company = root.length <= 2
    ? root.toUpperCase()
    : root[0].toUpperCase() + root.slice(1);
  return {
    company: company || "your team",
    hostname: host,
    address: `hello@${host}`,
    url: `https://${host}`,
  };
}

function stashTrialContext(hostname: string | null, preview?: TrialResult | null) {
  try {
    if (hostname && typeof window !== "undefined") {
      localStorage.setItem("autopitch.trial_domain", hostname);
      sessionStorage.setItem("autopitch.trial_domain", hostname);
    }
    if (preview && typeof window !== "undefined") {
      const payload = JSON.stringify({
        hostname,
        preview,
        savedAt: Date.now(),
      });
      localStorage.setItem(TRIAL_PREVIEW_STORAGE_KEY, payload);
      sessionStorage.setItem(TRIAL_PREVIEW_STORAGE_KEY, payload);
    }
  } catch { /* ignore */ }
}

const COOLDOWN_MS = 60_000;
const COOLDOWN_KEY = "ap_trial_last_attempt";

const PAYOFF_CHIPS = [
  { icon: Target, label: "ICP" },
  { icon: Sparkles, label: "Pains" },
  { icon: Wand2, label: "Angle" },
  { icon: Zap, label: "Opener" },
];

const EXAMPLES: { value: string; type: "url" | "description" }[] = [
  { value: "acme.com", type: "url" },
  { value: "linear.app", type: "url" },
  { value: "We sell HR software to 100–500p companies", type: "description" },
];

export const TrainingTrial = forwardRef<
  TrainingTrialHandle,
  {
    onSignup: () => void;
    showHeader?: boolean;
    onStateChange?: (kind: "idle" | "loading" | "result" | "error") => void;
  }
>(function TrainingTrial({ onSignup, showHeader = true, onStateChange }, ref) {
  const [input, setInput] = useState("");
  const [state, setState] = useState<State>({ kind: "idle" });
  const [now, setNow] = useState(Date.now());
  const [isFocused, setIsFocused] = useState(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const viewedRef = useRef(false);
  const autofocusedRef = useRef(false);

  // Notify parent (e.g. hero layout) so it can re-flow when the trial expands.
  useEffect(() => {
    onStateChange?.(state.kind);
  }, [state.kind, onStateChange]);


  useImperativeHandle(ref, () => ({
    focusInput: (source = "scroll") => {
      const el = inputRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      void trackEvent("trial_input_focused", { source });
    },
  }));

  const track: TrackFn = (name, props) => {
    void trackEvent(name, props);
  };

  // Fire trial_section_viewed once + auto-focus on desktop
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || viewedRef.current || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !viewedRef.current) {
            viewedRef.current = true;
            track("trial_section_viewed", {
              page: typeof window !== "undefined" ? window.location.pathname : null,
            });
            // Auto-focus on desktop only (≥768px) to avoid forcing the mobile keyboard open
            if (
              !autofocusedRef.current &&
              typeof window !== "undefined" &&
              window.matchMedia?.("(min-width: 768px)").matches &&
              inputRef.current &&
              !input
            ) {
              autofocusedRef.current = true;
              inputRef.current.focus({ preventScroll: true });
              track("trial_input_autofocused", {});
            }
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // tick for cooldown countdown
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  const cooldownRemainingMs = useMemo(() => {
    try {
      const last = Number(localStorage.getItem(COOLDOWN_KEY) || 0);
      const remaining = last + COOLDOWN_MS - now;
      return remaining > 0 ? remaining : 0;
    } catch {
      return 0;
    }
  }, [now]);

  // Helper: advance loading stages without losing other state.
  const setStage = (stage: Stage, status: StageStatus) => {
    setState((s) =>
      s.kind === "loading"
        ? { ...s, stages: { ...s.stages, [stage]: status } }
        : s,
    );
  };

  const looksLikeUrl = (v: string) => {
    const t = v.trim();
    if (!t) return false;
    if (/^https?:\/\//i.test(t)) return true;
    return /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/.*)?$/i.test(t);
  };

  const submit = async () => {
    const value = input.trim();
    if (!value) return;
    if (cooldownRemainingMs > 0) {
      track("trial_attempt_throttled", {
        cooldown_remaining_sec: Math.ceil(cooldownRemainingMs / 1000),
      });
      return;
    }

    try {
      localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }

    const startedAt =
      typeof performance !== "undefined" ? performance.now() : Date.now();

    setState({
      kind: "loading",
      stages: { ...INITIAL_STAGES },
      startedAt: Date.now(),
    });

    const isUrl = looksLikeUrl(value);
    const inputType: "url" | "description" = isUrl ? "url" : "description";
    const inputHostname = isUrl ? deriveHostname(value) : null;

    // Stash immediately so a later refresh or bail-out still resumes onboarding
    // without another scan (used by activation-repair-user for anon provisioning).
    try {
      if (inputHostname && typeof window !== "undefined") {
        localStorage.setItem("autopitch.trial_domain", inputHostname);
        sessionStorage.setItem("autopitch.trial_domain", inputHostname);
      }
    } catch { /* ignore */ }

    track("trial_attempt_started", {
      input_type: inputType,
      hostname: inputHostname,
      input_length: value.length,
    });

    // Optimistic stage hand-off (only relevant before the QUICK call lands).
    const scrapeDoneTimer = window.setTimeout(() => {
      setState((s) =>
        s.kind === "loading" && s.stages.scrape === "active"
          ? {
              ...s,
              stages: { ...s.stages, scrape: "done", analyze: "active" },
            }
          : s,
      );
    }, 2500);

    const body = isUrl ? { url: value } : { description: value };

    // Track whether each call has settled so we can decide error semantics.
    let quickSucceeded = false;
    let messageSucceeded = false;
    let fullSucceeded = false;

    const elapsed = () =>
      Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt);

    // ── 1. QUICK call — Revenue Gap + Live Targets in ~1.5–3s ──
    const quickPromise = supabase.functions
      .invoke("training-trial-quick", { body })
      .then(({ data, error }) => {
        if (error || !data?.success || !data.preview) {
          track("trial_quick_failed", {
            reason: error ? "server" : "empty",
            latency_ms: elapsed(),
            hostname: inputHostname,
          });
          return;
        }
        quickSucceeded = true;
        const resolvedHostname = data.source?.hostname ?? inputHostname ?? null;
        track("trial_quick_succeeded", {
          hostname: resolvedHostname,
          latency_ms: elapsed(),
          targets: Array.isArray(data.preview.liveTargets) ? data.preview.liveTargets.length : 0,
        });
        window.clearTimeout(scrapeDoneTimer);
        setState((prev) => {
          // If the full payload already arrived, keep it as the source of truth.
          if (prev.kind === "result" && !prev.pending) return prev;
          const existing = prev.kind === "result" ? prev.data : ({} as TrialResult);
          return {
            kind: "result",
            hostname: resolvedHostname,
            pending: !(messageSucceeded && fullSucceeded),
            data: {
              ...existing,
              icp: existing.icp ?? data.preview.icp,
              revenueGap: data.preview.revenueGap ?? existing.revenueGap,
              liveTargets: data.preview.liveTargets ?? existing.liveTargets,
            } as TrialResult,
          };
        });
      })
      .catch((e) => {
        track("trial_quick_failed", {
          reason: "network",
          latency_ms: elapsed(),
          hostname: inputHostname,
          message: (e as { message?: string })?.message?.slice(0, 140),
        });
      });

    // ── 2. MESSAGE call — Email + branded Sender (~3-4s) ──
    // Renders the email card + inline booking-page preview much sooner than
    // the heavy `training-trial-preview` call, which also drafts the 4-step
    // sequence + one-pager. This is the conversion moment, so we ship it fast.
    const messagePromise = supabase.functions
      .invoke("training-trial-message", { body })
      .then(({ data, error }) => {
        if (error || !data?.success || !data.preview) {
          track("trial_message_failed", {
            reason: error ? "server" : "empty",
            latency_ms: elapsed(),
            hostname: inputHostname,
          });
          return;
        }
        messageSucceeded = true;
        const resolvedHostname = data.source?.hostname ?? inputHostname ?? null;
        track("trial_message_succeeded", {
          hostname: resolvedHostname,
          latency_ms: elapsed(),
          has_email: !!data.preview.email,
          has_sender_brand: !!data.preview.sender?.primaryColor,
        });
        window.clearTimeout(scrapeDoneTimer);
        setState((prev) => {
          const existing = prev.kind === "result" ? prev.data : ({} as TrialResult);
          return {
            kind: "result",
            hostname: resolvedHostname,
            pending: !fullSucceeded,
            data: {
              ...existing,
              // Message owns email + sender (faster + branded)
              email: data.preview.email ?? existing.email,
              sender: data.preview.sender ?? existing.sender,
              // Quick wins for icp if it landed first; otherwise message provides it.
              icp: existing.icp ?? data.preview.icp,
            } as TrialResult,
          };
        });
      })
      .catch((e) => {
        track("trial_message_failed", {
          reason: "network",
          latency_ms: elapsed(),
          hostname: inputHostname,
          message: (e as { message?: string })?.message?.slice(0, 140),
        });
      });

    // ── 2. FULL call — email, sequence, playground, etc. (~6–8s) ──
    const fullPromise = supabase.functions
      .invoke("training-trial-preview", { body })
      .then(({ data, error }) => {
        window.clearTimeout(scrapeDoneTimer);
        const latencyMs = elapsed();

        if (error) {
          const msg =
            (error as { message?: string })?.message ||
            "We couldn't read that. Try a different URL or paste a 1-line description.";
          track("trial_attempt_failed", {
            reason: "server",
            message: msg.slice(0, 140),
            latency_ms: latencyMs,
            hostname: inputHostname,
          });
          // Only surface error if quick ALSO failed — otherwise leave the
          // partial result on screen and just stop showing the spinner.
          if (!quickSucceeded && !messageSucceeded) {
            setState({ kind: "error", message: msg });
          } else {
            setState((prev) =>
              prev.kind === "result" ? { ...prev, pending: false } : prev,
            );
          }
          return;
        }
        if (!data || !data.success || !data.preview) {
          track("trial_attempt_failed", {
            reason: "empty",
            latency_ms: latencyMs,
            hostname: inputHostname,
          });
          if (!quickSucceeded && !messageSucceeded) {
            setState({
              kind: "error",
              message: "We couldn't generate a preview. Try a different URL or short description.",
            });
          } else {
            setState((prev) =>
              prev.kind === "result" ? { ...prev, pending: false } : prev,
            );
          }
          return;
        }

        fullSucceeded = true;
        const resolvedHostname = data.source?.hostname ?? inputHostname ?? null;
        track("trial_attempt_succeeded", {
          hostname: resolvedHostname,
          latency_ms: latencyMs,
          pains_count: Array.isArray(data.preview.pains) ? data.preview.pains.length : 0,
          opener_lines: Array.isArray(data.preview.opener) ? data.preview.opener.length : 0,
        });

        setState((prev) => {
          // Merge full payload UNDER any partial quick / message payload already
          // on screen. Quick owns icp/revenueGap/liveTargets; message owns
          // email/sender. Full owns sequence/onePagerContent/pains/angle.
          const existing = prev.kind === "result" ? prev.data : ({} as TrialResult);
          return {
            kind: "result",
            hostname: resolvedHostname,
            pending: false,
            data: {
              ...data.preview,
              ...existing,
              // Full IS authoritative for the heavy fields it owns:
              sequence: data.preview.sequence ?? existing.sequence,
              pains: data.preview.pains ?? existing.pains,
              angle: data.preview.angle ?? existing.angle,
              opener: data.preview.opener ?? existing.opener,
              // Prefer message's email/sender (faster + always present); fall
              // back to full's if message failed.
              email: existing.email ?? data.preview.email,
              sender: existing.sender ?? data.preview.sender,
              // Lock in quick's values if present:
              icp: existing.icp ?? data.preview.icp,
              revenueGap: existing.revenueGap ?? data.preview.revenueGap,
              liveTargets: existing.liveTargets ?? data.preview.liveTargets,
            } as TrialResult,
          };
        });
      })
      .catch((e) => {
        window.clearTimeout(scrapeDoneTimer);
        const latencyMs = elapsed();
        console.error("training trial invoke failed", e);
        const msg = (e as { message?: string })?.message || "network error";
        track("trial_attempt_failed", {
          reason: "network",
          message: msg.slice(0, 140),
          latency_ms: latencyMs,
          hostname: inputHostname,
        });
        if (!quickSucceeded && !messageSucceeded) {
          setState({
            kind: "error",
            message: "Network hiccup. Try again in a moment.",
          });
        } else {
          setState((prev) =>
            prev.kind === "result" ? { ...prev, pending: false } : prev,
          );
        }
      });

    // Don't await — let all three promises settle independently and progressively
    // update state. We keep the function async for symmetry with the old API
    // (callers don't rely on the return value, but jsx-eslint expects it).
    void Promise.allSettled([quickPromise, messagePromise, fullPromise]);
  };

  const reset = () => {
    setState({ kind: "idle" });
  };

  const handleExampleClick = (ex: { value: string; type: "url" | "description" }) => {
    setInput(ex.value);
    track("trial_example_clicked", { example: ex.value, type: ex.type });
    // Focus + move caret to end
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      try {
        el.setSelectionRange(ex.value.length, ex.value.length);
      } catch {
        /* ignore */
      }
    });
  };

  const cooldownSec = Math.ceil(cooldownRemainingMs / 1000);
  const submitDisabled =
    !input.trim() || state.kind === "loading" || cooldownRemainingMs > 0;
  const isLoading = state.kind === "loading";

  return (
    <div ref={sectionRef} className="space-y-6 md:space-y-8">
      {/* Tightened header — input is the hero. Suppressed when embedded in hero. */}
      {showHeader && (
        <Reveal stagger={50} className="text-center space-y-3 max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight leading-[1.15]">
            We found where <span className="text-primary">your</span> next deals are.
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Drop your URL. See the accounts, the angle, and the opener in ~8 seconds.
          </p>
        </Reveal>
      )}

      {/* COMMAND BAR — the centerpiece */}
      <Reveal className="max-w-2xl mx-auto">
        <div
          className="relative rounded-2xl border border-primary/30 bg-card/80 backdrop-blur-xl p-3 sm:p-4 space-y-3 transition-shadow"
          style={{
            boxShadow: isFocused
              ? "0 0 0 1px hsl(var(--primary) / 0.35), 0 28px 80px -20px hsl(var(--primary) / 0.45)"
              : "0 0 0 1px hsl(var(--primary) / 0.08), 0 24px 70px -24px hsl(var(--primary) / 0.35)",
          }}
        >
          {/* Live micro-signal pinned to the card itself */}
          <div className="flex items-center justify-between gap-2 px-1">
            <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full bg-success"
                style={{
                  boxShadow: "0 0 6px hsl(var(--success) / 0.7)",
                  animation: "breathe-soft 2.4s ease-in-out infinite",
                }}
              />
              Live · powered by your site
            </div>
            <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70">
              Free · no signup · ~8s
            </span>
          </div>

          {/* The bar */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-2 sm:items-stretch">
            {/* Input wrapper with inline icon + (desktop) inline submit */}
            <div className="relative flex-1">
              <Globe
                aria-hidden
                className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors ${
                  isFocused || input ? "text-primary" : "text-muted-foreground/85"
                }`}
              />
              {/* Blinking caret cue when empty + unfocused */}
              {!input && !isFocused && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute left-12 top-1/2 -translate-y-1/2 text-primary/80 text-base sm:text-lg font-mono select-none"
                  style={{ animation: "breathe-soft 1.2s ease-in-out infinite" }}
                >
                  ▎
                </span>
              )}
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                }}
                placeholder={!input && !isFocused ? "    yourcompany.com" : "yourcompany.com"}
                maxLength={600}
                disabled={isLoading}
                inputMode="url"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                aria-label="Your company website"
                className="w-full h-14 sm:h-16 rounded-xl bg-background/70 border border-border/60 pl-12 pr-4 sm:pr-44 text-base sm:text-lg text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              />
              {/* Inline submit on sm+ */}
              <div className="hidden sm:block absolute right-2 top-1/2 -translate-y-1/2">
                <MagneticButton strength={3}>
                  <Button
                    size="lg"
                    className="h-12 px-5 gap-2 glow-primary"
                    onClick={submit}
                    disabled={submitDisabled}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Analyzing
                      </>
                    ) : (
                      <>
                        Generate <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </MagneticButton>
              </div>
            </div>

            {/* Stacked submit on mobile only */}
            <MagneticButton strength={4} className="sm:hidden">
              <Button
                size="lg"
                className="h-14 w-full px-6 gap-2 glow-primary text-base"
                onClick={submit}
                disabled={submitDisabled}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Analyzing
                  </>
                ) : (
                  <>
                    Generate my preview <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </MagneticButton>
          </div>

          {/* (Payoff chips removed — output sections are now the proof.) */}

          {/* Mobile-only cooldown notice (no redundant "free / no signup" line) */}
          {cooldownRemainingMs > 0 && !isLoading && (
            <p className="sm:hidden text-center text-[10px] font-mono uppercase tracking-widest text-amber-500/90">
              Try again in {cooldownSec}s
            </p>
          )}
          {cooldownRemainingMs > 0 && !isLoading && (
            <p className="hidden sm:block text-center text-[10px] font-mono uppercase tracking-widest text-amber-500/90">
              Try again in {cooldownSec}s
            </p>
          )}
        </div>
      </Reveal>

      {/* Output area */}
      <div className="max-w-3xl mx-auto">
        {state.kind === "idle" && <IdleHint examples={EXAMPLES} onPick={handleExampleClick} />}
        {state.kind === "loading" && (
          <LoadingSkeleton stages={state.stages} startedAt={state.startedAt} />
        )}
        {state.kind === "error" && <ErrorPanel message={state.message} onRetry={reset} />}
        {state.kind === "result" && (
          <ResultPanel
            data={state.data}
            hostname={state.hostname}
            pending={state.pending ?? false}
            onSignup={onSignup}
            onReset={reset}
            track={track}
          />
        )}
      </div>
    </div>
  );
});

/* ------------------------------------------------------------------ */
/*  Subcomponents                                                      */
/* ------------------------------------------------------------------ */

function IdleHint({
  examples,
  onPick,
}: {
  examples: { value: string; type: "url" | "description" }[];
  onPick: (ex: { value: string; type: "url" | "description" }) => void;
}) {
  return (
    <div className="text-center space-y-2 text-xs text-muted-foreground">
      <p className="font-mono uppercase tracking-widest text-[10px]">or try one of these</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {examples.map((ex) => (
          <button
            key={ex.value}
            type="button"
            onClick={() => onPick(ex)}
            className="px-2.5 py-1 rounded border border-border/60 bg-card/40 font-mono text-[11px] text-foreground/80 hover:text-foreground hover:border-primary/40 hover:bg-primary/5 transition-colors"
          >
            {ex.value}
          </button>
        ))}
      </div>
    </div>
  );
}

const STAGE_META: Record<Stage, { label: string; sublabel: string }> = {
  scrape: {
    label: "Reading your site",
    sublabel: "Pulling brand colors, logo, and positioning",
  },
  analyze: {
    label: "Analyzing your buyers",
    sublabel: "Drafting ICP, pains, angle, and a real opener",
  },
};

function LoadingSkeleton({
  stages,
  startedAt,
}: {
  stages: StageState;
  startedAt: number;
}) {
  // Local elapsed timer — visible accountability for the wait.
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setElapsedSec(Math.max(0, Math.round((Date.now() - startedAt) / 1000)));
    }, 250);
    return () => clearInterval(id);
  }, [startedAt]);

  const order: Stage[] = ["scrape", "analyze"];
  const doneCount = order.filter((s) => stages[s] === "done").length;
  const activeStage = order.find((s) => stages[s] === "active") ?? null;
  const progressPct = Math.min(
    95,
    Math.round((doneCount / order.length) * 100) + (activeStage ? 12 : 0),
  );

  return (
    <div className="space-y-4">
      {/* Header: stage list + elapsed */}
      <div className="rounded-xl border border-primary/20 bg-card/50 p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[10px] font-mono uppercase tracking-widest text-primary-glow">
            Generating your preview
          </p>
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground tabular-nums">
            {elapsedSec}s elapsed
          </p>
        </div>

        {/* Real progress bar */}
        <div className="h-1 w-full bg-muted/30 rounded overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary via-cyan-400 to-emerald-400 transition-[width] duration-500 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Stage rows */}
        <ul className="space-y-1.5">
          {order.map((s) => {
            const status = stages[s];
            return (
              <li
                key={s}
                className="flex items-start gap-2.5 text-xs"
                aria-current={status === "active" ? "step" : undefined}
              >
                <span
                  aria-hidden
                  className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                    status === "done"
                      ? "border-primary bg-primary text-primary-foreground"
                      : status === "active"
                      ? "border-primary/60 bg-primary/10"
                      : "border-border bg-muted/20"
                  }`}
                >
                  {status === "done" ? (
                    <Check className="h-2.5 w-2.5" />
                  ) : status === "active" ? (
                    <Loader2 className="h-2.5 w-2.5 animate-spin text-primary" />
                  ) : null}
                </span>
                <span className="flex-1 leading-tight">
                  <span
                    className={`block font-medium ${
                      status === "pending" ? "text-muted-foreground/85" : "text-foreground"
                    }`}
                  >
                    {STAGE_META[s].label}
                  </span>
                  <span className="block text-[11px] text-muted-foreground/80">
                    {STAGE_META[s].sublabel}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* (Filmstrip skeleton removed — Revenue Gap lands first and slots in directly.) */}
    </div>
  );
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5 text-center space-y-3">
      <p className="text-sm text-foreground">{message}</p>
      <Button size="sm" variant="outline" onClick={onRetry} className="gap-1.5">
        <RefreshCw className="h-3.5 w-3.5" /> Try again
      </Button>
    </div>
  );
}

/**
 * Stash the scanned domain and hand the visitor off to /onboarding under an
 * anonymous session. Shared between the auto-launch effect, the sticky mobile
 * CTA, and the SignupCard fallback button.
 */
async function launchAnonymousOnboarding(hostname: string | null, preview?: TrialResult | null): Promise<void> {
  stashTrialContext(hostname, preview);
  try {
    const { data: existing } = await supabase.auth.getSession();
    if (!existing.session) {
      const { error } = await supabase.auth.signInAnonymously({
        options: { data: { trial_domain: hostname ?? undefined } },
      });
      if (error) throw error;
      void trackEvent("trial_anonymous_signin", { status: "success", hostname });
    }
  } catch (err: any) {
    console.warn("[trial] anonymous sign-in failed", err);
    void trackEvent("trial_anonymous_signin", { status: "error", message: err?.message });
    throw err;
  }
  window.location.assign("/onboarding");
}

function ResultPanel({
  data,
  hostname,
  pending,
  onSignup,
  onReset,
  track,
}: {
  data: TrialResult;
  hostname: string | null;
  pending: boolean;
  onSignup: () => void;
  onReset: () => void;
  track: TrackFn;
}) {
  // Lift data into mutable state so the playground can refine it live.
  const [liveData, setLiveData] = useState<TrialResult>(data);
  // Reset whenever the parent generates a fresh preview.
  useEffect(() => {
    setLiveData(data);
  }, [data]);

  const handleUnlock = () => {
    track("trial_unlock_clicked", { hostname, had_result: true, auto: false });
    void launchAnonymousOnboarding(hostname, liveData).catch(() => { /* toast in card */ });
  };
  const handleTryAnother = () => {
    track("trial_try_another_clicked", { hostname });
    onReset();
  };

  // Refs for sticky CTA visibility logic
  const signupCardRef = useRef<HTMLDivElement | null>(null);

  // Auto-launch: as soon as the preview has usable data (email drafted), hand
  // the visitor straight into /onboarding under an anonymous session. Countdown
  // gives them a beat to read + a "Not now" bail-out.
  const AUTO_LAUNCH_MS = 3500;
  const [autoLaunching, setAutoLaunching] = useState(false);
  const [autoLaunchCancelled, setAutoLaunchCancelled] = useState(false);
  const [autoRemaining, setAutoRemaining] = useState<number | null>(null);
  const autoTriggeredRef = useRef(false);

  useEffect(() => {
    if (autoTriggeredRef.current) return;
    if (autoLaunchCancelled) return;
    if (!hostname || !liveData?.email) return;
    autoTriggeredRef.current = true;
    setAutoRemaining(Math.ceil(AUTO_LAUNCH_MS / 1000));
    const startedAt = Date.now();
    const tick = window.setInterval(() => {
      const left = Math.max(0, Math.ceil((AUTO_LAUNCH_MS - (Date.now() - startedAt)) / 1000));
      setAutoRemaining(left);
      if (left <= 0) window.clearInterval(tick);
    }, 250);
    const timer = window.setTimeout(() => {
      window.clearInterval(tick);
      if (autoLaunchCancelled) return;
      setAutoLaunching(true);
      track("trial_unlock_clicked", { hostname, had_result: true, auto: true });
      void launchAnonymousOnboarding(hostname, liveData).catch(() => {
        setAutoLaunching(false);
      });
    }, AUTO_LAUNCH_MS);
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(timer);
    };
  }, [hostname, liveData?.email, autoLaunchCancelled, track]);

  const cancelAutoLaunch = () => {
    setAutoLaunchCancelled(true);
    setAutoRemaining(null);
    track("trial_auto_launch_cancelled", { hostname });
  };

  return (
    <div className="space-y-4 animate-fade-in pb-24 md:pb-0">
      {hostname && (
        <p className="text-center text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          based on · {hostname}
        </p>
      )}

      {/* Auto-launch banner — fires ~3s after preview is ready */}
      {autoRemaining !== null && !autoLaunchCancelled && (
        <div
          className="flex flex-wrap items-center justify-center gap-3 rounded-xl border border-primary/30 bg-primary/[0.06] px-4 py-2.5 text-xs"
          role="status"
          aria-live="polite"
        >
          <span className="flex items-center gap-2 text-primary">
            {autoLaunching ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening your onboarding…</>
            ) : (
              <><Sparkles className="h-3.5 w-3.5" /> Preparing your onboarding session in {autoRemaining}s…</>
            )}
          </span>
          {!autoLaunching && (
            <button
              type="button"
              className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2"
              onClick={cancelAutoLaunch}
            >
              Not now — keep reading
            </button>
          )}
        </div>
      )}

      {/* Revenue Gap and Live Targets removed — felt like noise. */}

      {/* "Still drafting" pill while the heavy email + sequence is in flight */}
      {pending && (
        <div className="flex items-center justify-center pt-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/25 bg-primary/[0.06] text-[10px] font-mono uppercase tracking-widest text-primary/90">
            <Loader2 className="h-3 w-3 animate-spin" />
            Drafting your email and 5-step sequence…
          </div>
        </div>
      )}

      {/* 3. Email + booking preview — the "wow" */}
      {liveData.email ? (
        <div className="pt-2 space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70 text-center">
            here's the first email we'd send · and where they'd land
          </p>
          <EmailMockCard
            email={liveData.email}
            opener={liveData.opener}
            hostname={hostname}
            sender={liveData.sender ?? clientDeriveSender(hostname)}
            icpTitle={liveData.icp?.title}
          />
        </div>
      ) : pending ? (
        <SectionSkeleton label="Drafting your first email + booking page" rows={4} />
      ) : null}

      {/* 4. Sequence stack — proof of depth */}
      {liveData.email ? (
        <SequenceStack
          firstSubject={liveData.email?.subject}
          firstTeaser={liveData.email?.hook}
          sequence={liveData.sequence}
          hostname={hostname}
          onUnlock={handleUnlock}
          track={track}
        />
      ) : pending ? (
        <SectionSkeleton label="5-step follow-up cadence" rows={3} />
      ) : null}

      {/* 5. PRIMARY conversion — inline account creation, no modal */}
      <div ref={signupCardRef}>
        <SignupCard
          hostname={hostname}
          preview={liveData}
          onTryAnother={handleTryAnother}
          track={track}
        />
      </div>

      {/* Sticky mobile CTA — scrolls to inline signup card */}
      <StickyMobileCTA
        signupCardRef={signupCardRef}
        closerRef={signupCardRef}
        onUnlock={() => {
          signupCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }}
        track={track}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Section skeleton — used while the heavy FULL payload is in flight  */
/* ------------------------------------------------------------------ */

function SectionSkeleton({ label, rows = 3 }: { label: string; rows?: number }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/40 p-5 relative overflow-hidden">
      <div className="flex items-center gap-2 mb-3">
        <Loader2 className="h-3 w-3 animate-spin text-primary/70" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
      </div>
      <div className="space-y-2">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="h-2.5 bg-muted/30 rounded"
            style={{ width: `${100 - i * 12}%` }}
          />
        ))}
      </div>
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.06), transparent)",
          animation: "shimmer-bar 1.6s ease-in-out infinite",
        }}
      />
    </div>
  );
}


/* ------------------------------------------------------------------ */
/*  Revenue Gap — tension anchor                                       */
/* ------------------------------------------------------------------ */

function formatUsdShort(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

function RevenueGapPanel({
  gap,
  hostname,
  track,
}: {
  gap: RevenueGap;
  hostname: string | null;
  track: TrackFn;
}) {
  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    track("trial_revenue_gap_shown", {
      hostname,
      accounts_missed: gap.accountsMissed,
    });
  }, [hostname, gap.accountsMissed, track]);

  return (
    <div
      className="relative rounded-2xl border border-emerald-500/40 bg-emerald-500/[0.04] p-5 sm:p-6 space-y-4 overflow-hidden"
      style={{
        boxShadow:
          "0 0 0 1px hsl(142 71% 45% / 0.18), 0 24px 70px -28px hsl(142 71% 45% / 0.35)",
      }}
    >
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-emerald-500">
        <span
          aria-hidden
          className="h-1.5 w-1.5 rounded-full bg-emerald-500"
          style={{
            boxShadow: "0 0 6px hsl(142 71% 45% / 0.7)",
            animation: "breathe-soft 2.4s ease-in-out infinite",
          }}
        />
        <TrendingDown className="h-3 w-3" />
        Revenue gap detected
      </div>

      <div className="space-y-1.5">
        <h3 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight leading-tight">
          You're missing ~<span className="text-emerald-500">{gap.accountsMissed.toLocaleString()}</span> high-fit accounts
        </h3>
        <p className="text-sm text-muted-foreground">
          Competitors in your space are actively targeting these segments today:
        </p>
      </div>

      <ul className="space-y-1.5 text-sm text-foreground/85 leading-relaxed">
        {gap.competitorTargeting.map((b, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-emerald-500 shrink-0 font-semibold">›</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] px-3.5 py-2.5 space-y-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-500/90">
            Estimated missed pipeline
          </span>
          <span className="text-base sm:text-lg font-bold text-foreground tabular-nums">
            {formatUsdShort(gap.pipelineLowUsd)}–{formatUsdShort(gap.pipelineHighUsd)}
            <span className="text-xs font-normal text-muted-foreground ml-1">/ year</span>
          </span>
        </div>
        {gap.assumedAcvUsdLow && gap.assumedAcvUsdHigh && gap.conversionRateLow && gap.conversionRateHigh && (
          <div className="text-[10px] font-mono text-muted-foreground/80 leading-relaxed">
            ~{gap.accountsMissed.toLocaleString()} accounts × {formatUsdShort(gap.assumedAcvUsdLow)}–{formatUsdShort(gap.assumedAcvUsdHigh)} ACV × {(gap.conversionRateLow * 100).toFixed(1)}–{(gap.conversionRateHigh * 100).toFixed(1)}% conversion
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Live Targets — deal access                                         */
/* ------------------------------------------------------------------ */

function LiveTargetsPanel({
  targets,
  hostname,
  totalUniverse,
  onUnlock,
  track,
}: {
  targets: LiveTarget[];
  hostname: string | null;
  totalUniverse?: number;
  onUnlock: () => void;
  track: TrackFn;
}) {
  const unlocked = targets.slice(0, 3);
  const locked = targets.slice(3, 8);
  // Total = derived from the AI's ICP universe estimate. If the server
  // didn't return one (older edge fn / fallback), fall back to the count
  // of targets we actually have so we never render a fake number.
  const total = totalUniverse && totalUniverse > targets.length ? totalUniverse : targets.length;
  const totalLabel = total.toLocaleString();

  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    track("trial_live_targets_shown", {
      hostname,
      unlocked_count: unlocked.length,
      locked_count: locked.length,
    });
  }, [hostname, unlocked.length, locked.length, track]);

  const handlePrimary = () => {
    track("trial_unlock_targets_clicked", { hostname });
    onUnlock();
  };
  const handleSecondary = () => {
    track("trial_unlock_full_list_clicked", { hostname });
    onUnlock();
  };

  return (
    <div
      className="relative rounded-2xl border border-primary/35 bg-card/70 backdrop-blur-xl p-5 sm:p-6 space-y-4"
      style={{
        boxShadow:
          "0 0 0 1px hsl(var(--primary) / 0.12), 0 24px 70px -28px hsl(var(--primary) / 0.35)",
      }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-primary">
          <span
            aria-hidden
            className="h-1.5 w-1.5 rounded-full bg-primary"
            style={{
              boxShadow: "0 0 6px hsl(var(--primary) / 0.7)",
              animation: "breathe-soft 2.4s ease-in-out infinite",
            }}
          />
          Live targets · matching your ICP right now
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70">
          {totalLabel} total · {unlocked.length} shown
        </span>
      </div>

      <ul className="space-y-1.5">
        {unlocked.map((t) => (
          <li
            key={t.company}
            className="flex items-center gap-3 rounded-lg border border-border/60 bg-background/40 px-3.5 py-2.5"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-foreground truncate">{t.company}</div>
              <div className="text-xs text-muted-foreground truncate">{t.signal}</div>
            </div>
            <span className="shrink-0 text-[10px] font-mono uppercase tracking-widest text-primary/90 px-2 py-0.5 rounded-full border border-primary/25 bg-primary/5">
              {t.matchPct}% match
            </span>
          </li>
        ))}

        {locked.map((t, i) => (
          <li
            key={`locked-${i}`}
            className="flex items-center gap-3 rounded-lg border border-border/60 bg-background/40 px-3.5 py-2.5"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-foreground truncate">{t.company}</div>
              <div className="text-xs text-muted-foreground truncate">{t.signal}</div>
            </div>
            <span className="shrink-0 text-[10px] font-mono uppercase tracking-widest text-primary/90 px-2 py-0.5 rounded-full border border-primary/25 bg-primary/5">
              {t.matchPct}% match
            </span>
          </li>
        ))}
      </ul>

      <div className="flex justify-center pt-1">
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2 text-xs"
          onClick={handleSecondary}
        >
          See full list ({totalLabel}) <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Signup card — primary post-preview conversion                     */
/* ------------------------------------------------------------------ */

function SignupCard({
  hostname,
  preview,
  onTryAnother,
  track,
}: {
  hostname: string | null;
  preview?: TrialResult | null;
  onTryAnother: () => void;
  track: TrackFn;
}) {
  const shownRef = useRef(false);
  const { toast } = useToast();
  const [launching, setLaunching] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<"google" | "microsoft" | null>(null);

  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    track("trial_signup_card_shown");
  }, [track]);

  const handleLaunch = async () => {
    if (launching || oauthLoading) return;
    setLaunching(true);
    stashTrialContext(hostname, preview);
    track("trial_unlock_clicked", { hostname, method: "anonymous" });
    try {
      await launchAnonymousOnboarding(hostname, preview);
    } catch (err: any) {
      toast({
        title: "Couldn't start onboarding",
        description: err?.message ?? "Please try again in a moment.",
        variant: "destructive",
      });
      setLaunching(false);
    }
  };

  const handleOAuth = async (provider: "google" | "microsoft") => {
    setOauthLoading(provider);
    stashTrialContext(hostname, preview);
    track("trial_unlock_clicked", { hostname, method: provider });
    try {
      const { error } = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: `${window.location.origin}/onboarding`,
      });
      if (error) throw error;
    } catch (err: any) {
      toast({
        title: `${provider === "google" ? "Google" : "Microsoft"} sign-in failed`,
        description: err?.message ?? "Please try again.",
        variant: "destructive",
      });
      setOauthLoading(null);
    }
  };

  return (
    <div
      className="relative rounded-2xl border border-primary/40 bg-card/80 backdrop-blur-xl p-5 sm:p-7 space-y-5"
      style={{
        boxShadow:
          "0 0 0 1px hsl(var(--primary) / 0.18), 0 28px 80px -20px hsl(var(--primary) / 0.45)",
      }}
    >
      <div className="flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-widest text-primary">
        <span
          aria-hidden
          className="h-1.5 w-1.5 rounded-full bg-primary"
          style={{
            boxShadow: "0 0 6px hsl(var(--primary) / 0.7)",
            animation: "breathe-soft 2.4s ease-in-out infinite",
          }}
        />
        Step 2 of 2 · Launch onboarding
      </div>

      <div className="text-center space-y-2 max-w-md mx-auto">
        <h3 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight leading-tight">
          Launch your AI SDR on <span className="text-primary">{hostname || "your site"}</span>
        </h3>
        <p className="text-sm text-muted-foreground">
          No signup up front — jump straight into onboarding. We'll ask for your email once your workspace is ready.
        </p>
      </div>

      <div className="max-w-sm mx-auto w-full space-y-3">
        <MagneticButton strength={4} className="w-full">
          <Button
            type="button"
            size="lg"
            className="h-12 w-full gap-2 glow-primary text-sm"
            disabled={launching || !!oauthLoading}
            onClick={handleLaunch}
          >
            {launching ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Creating your onboarding session…</>
            ) : (
              <>Launch onboarding <ArrowRight className="h-4 w-4" /></>
            )}
          </Button>
        </MagneticButton>

        <p className="text-center text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70">
          No card required · Free first qualified meeting
        </p>

        <div className="flex items-center gap-3 pt-2">
          <Separator className="flex-1" />
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">already have an account?</span>
          <Separator className="flex-1" />
        </div>

        <GoogleSignInButton onClick={() => handleOAuth("google")} disabled={!!oauthLoading || launching} />
        <MicrosoftSignInButton onClick={() => handleOAuth("microsoft")} disabled={!!oauthLoading || launching} />

        <div className="flex justify-center pt-1">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-muted-foreground hover:text-foreground"
            onClick={onTryAnother}
            type="button"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Try another URL
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Email capture                                                      */
/* ------------------------------------------------------------------ */

type CaptureState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "sent"; email: string }
  | { kind: "error"; message: string };

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

function getUtm(): { utm_source?: string; utm_medium?: string; utm_campaign?: string } {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || undefined,
    utm_medium: p.get("utm_medium") || undefined,
    utm_campaign: p.get("utm_campaign") || undefined,
  };
}

function EmailCapture({
  data,
  hostname,
  track,
}: {
  data: TrialResult;
  hostname: string | null;
  track: TrackFn;
}) {
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(true);
  const [state, setState] = useState<CaptureState>({ kind: "idle" });
  const shownRef = useRef(false);

  useEffect(() => {
    if (!shownRef.current) {
      shownRef.current = true;
      track("trial_email_capture_shown", { hostname });
    }
  }, [hostname, track]);

  const submit = async () => {
    const value = email.trim();
    if (!isValidEmail(value)) {
      setState({ kind: "error", message: "Please enter a valid email." });
      track("trial_email_capture_failed", { reason: "invalid_email" });
      return;
    }
    setState({ kind: "submitting" });
    track("trial_email_capture_submitted", { hostname, opt_in: optIn });
    try {
      const { data: resp, error } = await supabase.functions.invoke(
        "training-trial-email",
        {
          body: {
            email: value,
            preview: data,
            hostname,
            input_type: hostname ? "url" : "description",
            utm: getUtm(),
            marketing_opt_in: optIn,
          },
        },
      );
      if (error) {
        const msg = (error as { message?: string })?.message || "We couldn't send it. Try again.";
        track("trial_email_capture_failed", { reason: "server", message: msg.slice(0, 140) });
        setState({ kind: "error", message: msg });
        return;
      }
      if (!resp?.success) {
        const msg = resp?.message || "We couldn't send it. Try again.";
        track("trial_email_capture_failed", { reason: "server", message: msg.slice(0, 140) });
        setState({ kind: "error", message: msg });
        return;
      }
      track("trial_email_sent", { hostname, suppressed: !!resp.suppressed });
      setState({ kind: "sent", email: value });
    } catch (e) {
      track("trial_email_capture_failed", { reason: "network" });
      setState({ kind: "error", message: "Network hiccup. Try again." });
    }
  };

  if (state.kind === "sent") {
    return (
      <div className="rounded-xl border border-success/30 bg-success/5 p-4 sm:p-5 flex items-start gap-3 animate-fade-in">
        <div className="h-9 w-9 shrink-0 rounded-full bg-success/15 flex items-center justify-center">
          <Check className="h-4 w-4 text-success" />
        </div>
        <div className="text-sm">
          <p className="font-semibold text-foreground">Sent to {state.email}</p>
          <p className="text-muted-foreground">
            {optIn
              ? "Your preview is on the way. We'll send a few short follow-ups over the next few days — unsubscribe anytime."
              : "Check your inbox — your ICP, angle, and opener are on their way."}
          </p>
        </div>
      </div>
    );
  }

  const submitting = state.kind === "submitting";

  return (
    <div className="relative rounded-xl border border-border/60 bg-card/40 p-4 sm:p-5 space-y-3">
      {/* Compact prompt */}
      <p className="text-sm text-muted-foreground text-center sm:text-left">
        <span className="text-foreground font-medium">Not ready to sign up?</span>{" "}
        Email yourself the preview instead.
      </p>

      {/* Input + CTA — compact row */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-stretch w-full">
        <div className="relative flex-1">
          <Mail
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (state.kind === "error") setState({ kind: "idle" });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
            placeholder="you@company.com"
            maxLength={254}
            disabled={submitting}
            autoComplete="email"
            inputMode="email"
            aria-label="Your email address"
            className="w-full h-11 rounded-lg bg-background/70 border border-border/60 pl-10 pr-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          />
        </div>
        <Button
          variant="outline"
          size="default"
          className="h-11 w-full sm:w-auto px-4 gap-2 text-sm"
          onClick={submit}
          disabled={submitting || !email.trim()}
        >
          {submitting ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending
            </>
          ) : (
            <>
              Email it to me <ArrowRight className="h-3.5 w-3.5" />
            </>
          )}
        </Button>
      </div>

      {state.kind === "error" && (
        <p className="text-xs text-destructive">{state.message}</p>
      )}

      <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none">
        <input
          type="checkbox"
          checked={optIn}
          onChange={(e) => setOptIn(e.target.checked)}
          disabled={submitting}
          className="mt-0.5 h-3.5 w-3.5 rounded border-border/60 bg-background/60 text-primary focus:ring-1 focus:ring-primary/30 cursor-pointer"
        />
        <span>
          Send me a short Auto Pitch playbook (4 emails over 5 days). Unsubscribe in one click.
        </span>
      </label>

      <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/85 text-center sm:text-left">
        No spam · Unsubscribe in one click
      </p>
    </div>
  );
}

function Card({
  icon: Icon,
  eyebrow,
  title,
  why,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  eyebrow: string;
  title: string;
  why?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/60 backdrop-blur-md p-5 space-y-3 hover:border-primary/30 transition-colors">
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-primary" /> {eyebrow}
      </div>
      <h4 className="text-base font-semibold text-foreground leading-snug">{title}</h4>
      {children}
      {why && (
        <p className="text-[11px] text-muted-foreground/80 leading-relaxed border-t border-border/40 pt-2">
          {why}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Email mock — looks like a real inbox preview, with inline CTA      */
/* ------------------------------------------------------------------ */

function EmailMockCard({
  email,
  opener,
  hostname,
  sender,
  icpTitle,
}: {
  email?: EmailObject;
  opener: string[];
  hostname: string | null;
  sender: SenderIdentity;
  icpTitle?: string;
}) {
  const [bookingOpen, setBookingOpen] = useState(false);

  // Derive a workable email object from legacy opener lines if needed.
  // Signoff and subject are anchored to the sender — never "Auto Pitch".
  const e: EmailObject = email ?? {
    subject: hostname ? `two ideas for ${sender.company}` : "one thought on your workflow",
    greeting: "Hey Sarah,",
    hook: opener[0] ?? "",
    value: opener[1] ?? "",
    cta: opener[2] ?? "Want the 90-second teardown, or worth 12 min next Tuesday?",
    signoff: sender.company === "your team"
      ? "— The team"
      : `— The ${sender.company} team`,
    personaName: "Sarah",
    personaRole: "VP Revenue",
    personaCompany: "Northwind Logistics",
    whyNow: "Triggered by a recent expansion announcement on LinkedIn.",
  };

  const isPlaceholderSender = !sender.hostname;
  const personaLabel = e.personaName && e.personaRole && e.personaCompany
    ? `${e.personaName} · ${e.personaRole} at ${e.personaCompany}`
    : null;

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border/60 bg-background/60 overflow-hidden shadow-sm">
      {/* Header strip — looks like an email client */}
      <div className="border-b border-border/50 bg-card/40 px-3.5 py-3 space-y-1.5">
        <div className="text-[9.5px] uppercase tracking-[0.18em] text-muted-foreground/85 font-medium">
          Inbox preview
        </div>
        <div className="flex items-baseline gap-2 text-[11px] text-muted-foreground/90 min-w-0">
          <span className="h-1.5 w-1.5 rounded-full bg-primary/70 self-center shrink-0" aria-hidden />
          <span className="shrink-0 uppercase tracking-widest text-[9.5px] text-muted-foreground/85 font-mono">From</span>
          <span className="font-semibold text-foreground/90 shrink-0 truncate max-w-[40%]">{sender.company}</span>
          <span className="text-muted-foreground/70 truncate min-w-0 font-mono text-[10.5px]">
            &lt;{sender.address}&gt;
          </span>
        </div>
        {personaLabel && (
          <div className="flex items-baseline gap-2 text-[11px] text-muted-foreground/90 min-w-0">
            <span className="h-1.5 w-1.5 rounded-full bg-primary-glow/70 self-center shrink-0" aria-hidden />
            <span className="shrink-0 uppercase tracking-widest text-[9.5px] text-muted-foreground/85 font-mono">To</span>
            <span className="text-foreground/85 truncate min-w-0">{personaLabel}</span>
          </div>
        )}
        <div className="text-[15px] sm:text-base font-semibold tracking-tight text-foreground leading-snug">
          {e.subject}
        </div>
      </div>

      {/* Body — sans-serif, like a real email */}
      <div className="px-3.5 py-3.5 space-y-2.5 text-[13px] sm:text-sm leading-relaxed text-foreground/90">
        <p>{e.greeting}</p>
        <p>{e.hook}</p>
        <p>{e.value}</p>

        {/* Inline CTA — opens a preview of the visitor's branded booking page.
            Real prospects clicking this in the wild would land on the
            tenant's hosted /book/:slug page. Here we mock that page so the
            visitor can see what their prospects would experience. */}
        <p className="pt-1">
          {sender.hostname ? (
            <button
              type="button"
              onClick={() => setBookingOpen(true)}
              className="inline-flex items-center gap-1.5 text-primary font-medium underline decoration-primary/40 underline-offset-4 decoration-2 hover:decoration-primary transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
              aria-label={`Preview ${sender.company}'s branded booking page`}
            >
              {e.cta}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 text-primary font-medium underline decoration-primary/40 underline-offset-4 decoration-2"
              aria-label="Sample call-to-action"
            >
              {e.cta}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </span>
          )}
        </p>

        <p className="pt-2 text-foreground/85">{e.signoff}</p>

        {e.ps && (
          <p className="pt-1 text-[12px] sm:text-[12.5px] italic text-muted-foreground leading-relaxed">
            {e.ps}
          </p>
        )}
      </div>

      {/* Why-this-email reasoning panel — proves AutoPitch is reasoning, not spraying */}
      {e.whyNow && (
        <div className="border-t border-border/40 bg-primary/5 px-3.5 py-2.5 text-[11px] leading-relaxed">
          <div className="flex items-start gap-2">
            <Sparkles className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" aria-hidden />
            <div className="min-w-0">
              <div className="text-[9.5px] font-mono uppercase tracking-[0.18em] text-primary/80 mb-0.5">
                Why this email · why now
              </div>
              <p className="text-foreground/85">{e.whyNow}</p>
            </div>
          </div>
        </div>
      )}

      {/* Provenance footer — clarifies AutoPitch drafted it without polluting the body */}
      <div className="border-t border-border/40 bg-card/20 px-3.5 py-2 text-[10px] text-muted-foreground/70 leading-relaxed">
        {isPlaceholderSender ? (
          <>Add your website above to render this from your own domain.</>
        ) : (
          <>Drafted by AutoPitch · sample outbound from <span className="font-mono">{sender.hostname}</span></>
        )}
      </div>
      {/* close: email card */}

      {/* Inline booking page preview — flows directly under the email so
          the visitor experiences the full "send → land" journey without
          having to click the CTA. The lightbox below stays available for
          a closer look via the body's "Open full screen" affordance. */}
      {sender.hostname && (
        <>
          <div
            className="flex flex-col items-center gap-1 py-1 text-muted-foreground"
            aria-hidden
          >
            <ArrowDown className="h-3.5 w-3.5 text-primary/70" />
            <span className="text-[10px] uppercase tracking-[0.18em]">
              What your prospect lands on
            </span>
          </div>

          <BookingPagePreviewBody
            compact
            company={sender.company}
            hostname={sender.hostname || undefined}
            icpTitle={icpTitle}
            logoUrl={sender.logoUrl}
            primaryColor={sender.primaryColor}
            accentColor={sender.accentColor ?? sender.secondaryColor}
            fontFamily={sender.fontFamily}
            colorSource={sender.colorSource}
            logoSource={sender.logoSource}
            onOpenFullscreen={() => setBookingOpen(true)}
          />
        </>
      )}

      <BookingPagePreviewLightbox
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        company={sender.company}
        hostname={sender.hostname || undefined}
        icpTitle={icpTitle}
        logoUrl={sender.logoUrl}
        primaryColor={sender.primaryColor}
        accentColor={sender.accentColor ?? sender.secondaryColor}
        fontFamily={sender.fontFamily}
        colorSource={sender.colorSource}
        logoSource={sender.logoSource}
      />
    </div>
  </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SequenceStack — emails 2-5 with blur-with-peek                     */
/* ------------------------------------------------------------------ */

function SequenceStack({
  firstSubject,
  firstTeaser,
  sequence,
  hostname,
  onUnlock,
  track,
}: {
  firstSubject?: string;
  firstTeaser?: string;
  sequence?: SequenceStep[];
  hostname: string | null;
  onUnlock: () => void;
  track: TrackFn;
}) {
  const fallback: SequenceStep[] = [
    { day: 3, purpose: "Reframe", subject: "Different angle on {hook}", teaser: "Not chasing a reply — just reframing the problem in 2 lines." },
    { day: 6, purpose: "Proof", subject: "How {peer} solved this", teaser: "A 30-second case in their own words. No pitch attached." },
    { day: 10, purpose: "Single question", subject: "One question on {topic}", teaser: "Just asking if this maps to what your team owns right now." },
    { day: 15, purpose: "Soft close", subject: "Closing the loop", teaser: "If now isn't the time, no problem — happy to reconnect next quarter." },
  ];
  const steps = (sequence && sequence.length > 0 ? sequence : fallback).slice(0, 4);
  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    track("trial_sequence_stack_shown", { count: steps.length + 1 });
  }, [track, steps.length]);

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70">
            full 5-touch cadence
          </p>
          <h3 className="text-lg sm:text-xl font-bold text-foreground tracking-tight">
            The follow-ups that actually book the meeting
          </h3>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/85 hidden sm:inline">
          5 of 5
        </span>
      </div>

      <ol className="space-y-2">
        {/* Email 1 — visible */}
        <li className="rounded-xl border border-border/60 bg-card/60 p-3 sm:p-4">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-primary mb-1.5">
            <span className="rounded-sm bg-primary/15 px-1.5 py-0.5">Day 1</span>
            <span className="text-muted-foreground/70">· Opener</span>
            <span className="ml-auto inline-flex items-center gap-1 text-muted-foreground/70">
              <Check className="h-3 w-3" /> Visible
            </span>
          </div>
          <p className="text-sm font-semibold text-foreground line-clamp-1">
            {firstSubject || "One thought on your workflow"}
          </p>
          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
            {firstTeaser || "Personalized opener written for your buyer."}
          </p>
        </li>

        {/* Emails 2-5 — blurred peek */}
        {steps.map((s, i) => (
          <li key={i} className="rounded-xl border border-border/60 bg-card/60 p-3 sm:p-4">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-primary mb-1.5">
              <span className="rounded-sm bg-primary/15 px-1.5 py-0.5">Day {s.day}</span>
              <span className="text-muted-foreground/70">· {s.purpose}</span>
              <span className="ml-auto inline-flex items-center gap-1 text-muted-foreground/70">
                <Check className="h-3 w-3" /> Visible
              </span>
            </div>
            <p className="text-sm font-semibold text-foreground line-clamp-1">{s.subject}</p>
            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{s.teaser}</p>
          </li>
        ))}
      </ol>

      <p className="text-[11px] text-muted-foreground/70 text-center">
        Every step is A/Z tested and rewritten per reply — you'll see the variants in your dashboard.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  DayInLifeTimeline — Mon-Fri outcomes for the visitor               */
/* ------------------------------------------------------------------ */

function DayInLifeTimeline({
  icp,
  liveTargets,
  track,
}: {
  icp: { title: string; summary: string };
  liveTargets?: LiveTarget[];
  track: TrackFn;
}) {
  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    track("trial_dayinlife_shown");
  }, [track]);

  const sample = (liveTargets ?? []).slice(0, 3).map((t) => t.company);
  const sampleLabel = sample.length >= 2
    ? `${sample.slice(0, -1).join(", ")} & ${sample[sample.length - 1]}`
    : sample[0] || "your top accounts";

  const days: { day: string; label: string; icon: typeof Send; body: string }[] = [
    { day: "Mon", label: "Day 1", icon: Send, body: `Opener sent to ${sampleLabel} — and the rest of your ${icp.title.toLowerCase()} list.` },
    { day: "Tue", label: "Day 2", icon: Inbox, body: `First replies routed. AutoPitch handles every "not now," "wrong person," and "send info."` },
    { day: "Wed", label: "Day 3", icon: MessageSquare, body: `Reframe email goes out to anyone who didn't open — different angle, same buyer.` },
    { day: "Thu", label: "Day 4", icon: Sparkles, body: `Interested leads get qualifying questions. Warm ones get a calendar link.` },
    { day: "Fri", label: "Day 5", icon: Calendar, body: `First booked meetings hit your calendar. Weekly digest summarizes what worked.` },
  ];

  return (
    <div className="space-y-4">
      <div className="text-center space-y-1">
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70">
          week one with autopitch
        </p>
        <h3 className="text-lg sm:text-xl font-bold text-foreground tracking-tight">
          A day in the life of <span className="text-primary">your</span> AI SDR
        </h3>
      </div>

      <ol className="relative space-y-3 pl-6 sm:pl-8 before:absolute before:left-2 sm:before:left-3 before:top-2 before:bottom-2 before:w-px before:bg-gradient-to-b before:from-primary/40 before:via-primary/20 before:to-transparent">
        {days.map((d, i) => {
          const Icon = d.icon;
          return (
            <li key={i} className="relative">
              <span className="absolute -left-6 sm:-left-8 top-0.5 flex h-5 w-5 items-center justify-center rounded-full border border-primary/40 bg-background">
                <Icon className="h-2.5 w-2.5 text-primary" />
              </span>
              <div className="rounded-lg border border-border/50 bg-card/40 p-3">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-primary/80 mb-1">
                  <span>{d.day}</span>
                  <span className="text-muted-foreground/85">· {d.label}</span>
                </div>
                <p className="text-sm text-foreground/90 leading-snug">{d.body}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  StickyMobileCTA — appears after scrolling past signup card        */
/* ------------------------------------------------------------------ */

function StickyMobileCTA({
  signupCardRef,
  closerRef,
  onUnlock,
  track,
}: {
  signupCardRef: React.RefObject<HTMLDivElement>;
  closerRef: React.RefObject<HTMLDivElement>;
  onUnlock: () => void;
  track: TrackFn;
}) {
  const [visible, setVisible] = useState(false);
  const trackedRef = useRef(false);

  useEffect(() => {
    const signup = signupCardRef.current;
    if (!signup) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === signup) {
            // Show CTA only once user has scrolled past the signup card
            const past = entry.boundingClientRect.bottom < 0;
            setVisible(past);
            if (past && !trackedRef.current) {
              trackedRef.current = true;
              track("trial_sticky_cta_shown");
            }
          }
        }
      },
      { threshold: 0, rootMargin: "0px" }
    );
    io.observe(signup);
    return () => io.disconnect();
  }, [signupCardRef, closerRef, track]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 sm:hidden border-t border-primary/30 bg-background/95 backdrop-blur-xl px-4 py-3"
      style={{
        boxShadow: "0 -12px 40px -12px hsl(var(--primary) / 0.35)",
        paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
      }}
    >
      <Button
        size="lg"
        className="h-12 w-full gap-2 glow-primary"
        onClick={() => {
          track("trial_sticky_cta_click");
          onUnlock();
        }}
      >
        Launch onboarding <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

